const DEATH_MESSAGES = [
  'The dungeon claims another soul...',
  'Your bones will make a fine addition to the floor.',
  'Even the torches dimmed in mourning.',
  'The darkness swallows you whole.',
  'Another adventurer lost to hubris.',
  'The walls close in. The light fades.',
  'Your quest ends here, forgotten.',
  'The monsters feast tonight.',
  'Death was merely the beginning of your story.',
  'The dungeon grows stronger with each fallen hero.',
  'Your epitaph: "They went deeper than they should have."',
  'The rats will tell tales of your bravery. Briefly.',
  'A hero falls. The dungeon does not care.',
  'You have been weighed, measured, and found wanting.',
  'The abyss stared back. You blinked first.',
  'Not all who wander are lost. You, however, are very lost.',
  'The crypt keeper adds another name to the ledger.',
  'Your armor was not enough. Nothing ever is.',
  'Somewhere, a bard writes a cautionary verse about you.',
  'The floor is littered with those who thought themselves ready.',
  'The dungeon whispers your name to the next fool who enters.',
  'Greed led you deeper. Pride kept you from leaving.',
  'In the end, the treasure was not worth the price.',
  'Your torch flickers out. So does everything else.',
  'They warned you about the lower levels.',
  'The shadows remember your screams.',
  'Another soul for the collection.',
  'The dungeon thanks you for your generous donation of flesh.',
  'You died as you lived: surrounded by monsters.',
  'The gods look away. Some things are too painful even for them.',
  'Your final thought: "I should have brought more potions."',
  'The skeletons welcome you to their ranks.',
  'Legend says your ghost still wanders these halls.',
  'The spiders will nest in your empty helm.',
  'You were brave. Brave and foolish.',
  'The cold stone floor makes a poor deathbed.',
  'No songs will be sung. No tales will be told.',
  'The trap was obvious. In hindsight.',
  'Your map was upside down the entire time.',
  'The merchant warned you. You bought the cheap sword anyway.',
  'Death comes for all. It came for you on floor three.',
  'The mimics celebrate. You never suspected the door.',
  'Rest now, weary one. The dungeon will wait.',
  'Your coin purse clatters on the stone. Someone will find it.',
  'The necromancer smiles. Fresh material.',
  'You zigged when you should have zagged.',
  'The healing potion was right there. Three steps away.',
  'Respawn is a luxury most adventurers do not receive.',
  'The dungeon does not forgive. The dungeon does not forget.',
  'Your party will split your loot before the body cools.',
  'A critical failure at the worst possible moment.',
  'The RNG gods have forsaken you.',
  'Overconfidence is a slow and insidious killer.',
  'The flame that burns twice as bright dies in a dungeon.',
  'Your dodge roll had one too few invincibility frames.',
  'The boss had a second phase. You did not.',
  'You forgot to save. You always forget to save.',
  'The loot was legendary. Your survival was not.',
  'A moment of silence for the fallen. ...Okay, that is enough.',
  'The goblins will wear your boots. They are nice boots.',
  'You were this close to the exit. This close.',
  'The darkness does not discriminate between the brave and the foolish.',
  'Your journey ends. The dungeon endures.',
];

/** Drip positions (% from left) and heights per letter in "YOU DIED" */
const LETTER_DRIPS = {
  'Y': [{ x: 18, h: 28 }, { x: 50, h: 44 }, { x: 82, h: 24 }],
  'O': [{ x: 28, h: 22 }, { x: 55, h: 38 }, { x: 76, h: 28 }],
  'U': [{ x: 20, h: 32 }, { x: 50, h: 46 }, { x: 80, h: 26 }],
  'D': [{ x: 12, h: 38 }, { x: 72, h: 30 }],
  'I': [{ x: 50, h: 42 }],
  'E': [{ x: 12, h: 34 }, { x: 85, h: 22 }],
};

const GOTHIC_FONT_URL = 'https://fonts.googleapis.com/css2?family=Cinzel+Decorative:wght@700;900&family=Cinzel:wght@600;700&display=swap';

/**
 * HUDManager - Dungeon/combat overlay UI.
 * Creates and manages health bar, quickbar, objectives panel, minimap, and damage numbers.
 */
export default class HUDManager {
  constructor(options = {}) {
    this.player = options.player || null;
    this.inventory = options.inventory || null;
    this.visible = false;

    // Load gothic fonts (once)
    if (!document.getElementById('hud-gothic-font')) {
      const link = document.createElement('link');
      link.id = 'hud-gothic-font';
      link.rel = 'stylesheet';
      link.href = GOTHIC_FONT_URL;
      document.head.appendChild(link);
    }

    // Main container — append inside #container so HUD shares its stacking context
    // with game windows (z-index layering works correctly)
    this.container = document.createElement('div');
    this.container.className = 'game-hud';
    this.container.style.display = 'none';
    const gameContainer = document.getElementById('container');
    (gameContainer || document.body).appendChild(this.container);

    this._createHealthBar();
    this._createResourceBars();
    this._createGoldDisplay();
    this._createQuickbar();
    this._createDungeonInfo();

    /** @type {Array<{element: HTMLElement, lifetime: number}>} */
    this._damageNumbers = [];
  }

  show() {
    this.visible = true;
    this.container.style.display = '';
    if (this._resourceBars) this._resourceBars.style.display = '';
  }

  hide() {
    this.visible = false;
    this.container.style.display = 'none';
    if (this._resourceBars) this._resourceBars.style.display = 'none';
  }

  setPlayer(player) {
    this.player = player;
  }

  // ---- Health Bar ----

  _createHealthBar() {
    this.healthBar = document.createElement('div');
    this.healthBar.className = 'hud-health-bar';
    this.healthBar.innerHTML = `
      <div class="hud-health-bg">
        <div class="hud-health-fill"></div>
      </div>
      <span class="hud-health-text"></span>
    `;
    // Health bar hidden — replaced by floating over-character health bar
    this.healthBar.style.display = 'none';
    this.container.appendChild(this.healthBar);
    this._healthFill = this.healthBar.querySelector('.hud-health-fill');
    this._healthText = this.healthBar.querySelector('.hud-health-text');
  }

  _updateHealthBar() {
    if (!this.player?.combatStats) return;
    const stats = this.player.combatStats;
    const pct = (stats.health / stats.maxHealth) * 100;
    this._healthFill.style.width = `${pct}%`;

    if (pct > 60) this._healthFill.style.backgroundColor = '#22c55e';
    else if (pct > 30) this._healthFill.style.backgroundColor = '#eab308';
    else this._healthFill.style.backgroundColor = '#ef4444';

    this._healthText.textContent = `${Math.ceil(stats.health)} / ${stats.maxHealth}`;
  }

  // ---- Mana & Stamina Bars ----

  _createResourceBars() {
    this._resourceBars = document.createElement('div');
    this._resourceBars.className = 'hud-resource-bars';
    this._resourceBars.innerHTML = `
      <div class="hud-resource-bar stamina-bar">
        <div class="hud-resource-fill stamina-fill"></div>
        <span class="hud-resource-text stamina-text"></span>
      </div>
      <div class="hud-resource-bar mana-bar">
        <div class="hud-resource-fill mana-fill"></div>
        <span class="hud-resource-text mana-text"></span>
      </div>
    `;
    this._resourceBars.style.cssText = `
      position: fixed; bottom: 68px; left: 50%; transform: translateX(-50%);
      display: flex; gap: 6px; z-index: 5001; pointer-events: none;
    `;
    // Style each bar
    const barStyle = `
      width: 120px; height: 14px; border-radius: 7px;
      background: rgba(20,20,30,0.7); border: 1px solid rgba(255,255,255,0.15);
      position: relative; overflow: hidden;
    `;
    const fillStyle = `
      position: absolute; top: 0; left: 0; height: 100%; border-radius: 7px;
      transition: width 0.15s ease;
    `;
    const textStyle = `
      position: absolute; inset: 0; display: flex; align-items: center;
      justify-content: center; font-size: 10px; font-family: sans-serif;
      color: #fff; text-shadow: 0 1px 2px rgba(0,0,0,0.8); z-index: 1;
    `;
    for (const bar of this._resourceBars.querySelectorAll('.hud-resource-bar')) {
      bar.style.cssText = barStyle;
    }
    for (const fill of this._resourceBars.querySelectorAll('.hud-resource-fill')) {
      fill.style.cssText = fillStyle;
    }
    for (const text of this._resourceBars.querySelectorAll('.hud-resource-text')) {
      text.style.cssText = textStyle;
    }
    // Mana = blue, Stamina = yellow
    this._resourceBars.querySelector('.mana-fill').style.background = 'linear-gradient(90deg, #3b82f6, #60a5fa)';
    this._resourceBars.querySelector('.stamina-fill').style.background = 'linear-gradient(90deg, #ca8a04, #facc15)';

    // Start hidden (shown when HUD is shown)
    this._resourceBars.style.display = 'none';
    const gameContainer = document.getElementById('container');
    (gameContainer || document.body).appendChild(this._resourceBars);

    this._manaFill = this._resourceBars.querySelector('.mana-fill');
    this._manaText = this._resourceBars.querySelector('.mana-text');
    this._staminaFill = this._resourceBars.querySelector('.stamina-fill');
    this._staminaText = this._resourceBars.querySelector('.stamina-text');
  }

  _updateResourceBars() {
    if (!this.player?.combatStats) return;
    const s = this.player.combatStats;

    const manaPct = s.maxMana > 0 ? (s.mana / s.maxMana) * 100 : 0;
    this._manaFill.style.width = `${manaPct}%`;
    this._manaText.textContent = `${Math.ceil(s.mana)} / ${s.maxMana}`;

    const stamPct = s.maxStamina > 0 ? (s.stamina / s.maxStamina) * 100 : 0;
    this._staminaFill.style.width = `${stamPct}%`;
    this._staminaText.textContent = `${Math.ceil(s.stamina)} / ${s.maxStamina}`;
  }

  // ---- Gold Display ----

  _createGoldDisplay() {
    this._goldDisplay = document.createElement('div');
    this._goldDisplay.className = 'hud-gold-display';
    this._goldDisplay.innerHTML = `<img src="icons/items/I_GoldCoin.png" class="hud-gold-icon" alt=""><span class="hud-gold-text">0</span>`;
    this.container.appendChild(this._goldDisplay);
    this._goldText = this._goldDisplay.querySelector('.hud-gold-text');
  }

  updateGold(amount) {
    if (this._goldText) {
      this._goldText.textContent = amount;
    }
  }

  // ---- Quickbar ----

  _createQuickbar() {
    this.quickbar = document.createElement('div');
    this.quickbar.className = 'hud-quickbar';
    this._quickbarSlots = [];
    for (let i = 0; i < 8; i++) {
      const slot = document.createElement('div');
      slot.className = 'hud-quickbar-slot';
      slot.dataset.index = i;
      slot.addEventListener('click', () => {
        if (this.onSlotClick) this.onSlotClick(i);
      });
      this.quickbar.appendChild(slot);
      this._quickbarSlots.push(slot);
    }
    this.container.appendChild(this.quickbar);
  }

  updateQuickbar(inventory) {
    if (!inventory) return;
    for (let i = 0; i < this._quickbarSlots.length; i++) {
      const slot = this._quickbarSlots[i];
      const item = inventory.slots[i];
      // Clear previous content
      slot.innerHTML = '';
      slot.style.backgroundColor = '';
      slot.classList.remove('equipped');
      slot.title = '';

      if (item) {
        const c = item.def.color;
        if (c) {
          slot.style.backgroundColor = `rgba(${c.r},${c.g},${c.b}, 0.25)`;
        }
        // Item name tooltip (include exceptional prefix and item level)
        let tooltipName = item.def.name || item.itemId;
        if (item.instanceData?.exceptional) {
          tooltipName = `${item.instanceData.exceptional.prefix} ${tooltipName}`;
        }
        if (item.instanceData?.itemLevel > 1) {
          tooltipName += ` (iLvl ${item.instanceData.itemLevel})`;
        }
        slot.title = tooltipName;

        // Pixel art icon or letter fallback
        if (item.def.icon) {
          const icon = document.createElement('img');
          icon.src = item.def.icon;
          icon.className = 'slot-icon';
          icon.draggable = false;
          slot.appendChild(icon);
        } else {
          const label = document.createElement('span');
          label.style.fontSize = '14px';
          label.style.color = '#fff';
          label.textContent = (item.def.name || '?')[0].toUpperCase();
          slot.appendChild(label);
        }

        // Stack count
        if (item.quantity > 1) {
          const countEl = document.createElement('span');
          countEl.className = 'slot-count';
          countEl.textContent = item.quantity;
          slot.appendChild(countEl);
        }
      }
    }

    // Update gold display
    this.updateGold(inventory.gold || 0);
  }

  /** Callback when a quickbar slot is clicked. Set by main.js. */
  onSlotClick = null;

  // ---- Objectives (now handled by GameWindow) ----

  updateObjectives() {
    // No-op: objectives are now rendered in the objectives GameWindow
  }

  // ---- Dungeon Info ----

  _createDungeonInfo() {
    this.dungeonInfo = document.createElement('div');
    this.dungeonInfo.className = 'hud-dungeon-info';
    this.container.appendChild(this.dungeonInfo);
  }

  setDungeonInfo(text) {
    this.dungeonInfo.textContent = text;
  }

  updateDungeonInfo(levelText, themeName) {
    this.dungeonInfo.textContent = `${levelText} — ${themeName}`;
  }

  // ---- Damage Numbers ----

  showDamageNumber(target, damage) {
    const el = document.createElement('div');
    el.className = 'hud-damage-number';
    el.textContent = Math.round(damage);

    // Attach to target's sprite element for world depth-sorted rendering.
    // Falls back to HUD container if sprite unavailable.
    const spriteEl = target?.sprite?.element;
    if (spriteEl) {
      spriteEl.appendChild(el);
    } else {
      this.container.appendChild(el);
    }
    el.style.transform = 'translateX(-50%)';
    el.style.opacity = '1';
    this._damageNumbers.push({ element: el, lifetime: 0.8, age: 0 });
  }

  // ---- Level Complete ----

  showLevelComplete(message = 'LEVEL COMPLETE') {
    window.game?.audioSystem?.audio?.playUiFanfare();
    // Remove any existing banner
    const old = document.getElementById('level-complete-banner');
    if (old) old.remove();

    const wrap = document.createElement('div');
    wrap.id = 'level-complete-banner';
    wrap.style.cssText = `
      position: fixed; inset: 0; z-index: 9999;
      display: flex; align-items: center; justify-content: center;
      pointer-events: none;
      animation: lcFadeIn 0.3s ease-out;
    `;

    // Build echo layers + main text
    const ECHO_COUNT = 3;
    let echosHTML = '';
    for (let i = 0; i < ECHO_COUNT; i++) {
      const delay = i * 0.18;
      echosHTML += `<div class="lc-echo" style="animation-delay:${delay}s">${message}</div>`;
    }

    wrap.innerHTML = `
      <style>
        @keyframes lcFadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes lcFadeOut { from { opacity: 1; } to { opacity: 0; } }
        @keyframes lcReveal {
          0%   { opacity: 0; transform: scale(0.6); }
          50%  { opacity: 1; transform: scale(1.06); }
          70%  { transform: scale(0.98); }
          100% { transform: scale(1); }
        }
        @keyframes lcEchoExpand {
          0%   { opacity: 0.5; transform: scale(1); }
          100% { opacity: 0; transform: scale(2.2); }
        }
        @keyframes lcGlow {
          0%,100% { text-shadow: 0 0 20px rgba(255,215,0,0.4), 0 0 40px rgba(255,170,0,0.2), 0 2px 8px rgba(0,0,0,0.8); }
          50%     { text-shadow: 0 0 36px rgba(255,215,0,0.7), 0 0 72px rgba(255,170,0,0.35), 0 2px 8px rgba(0,0,0,0.8); }
        }
        @keyframes lcLine {
          0%   { width: 0; opacity: 0; }
          50%  { opacity: 1; }
          100% { width: 120px; opacity: 0.6; }
        }
        @keyframes lcSubReveal {
          0%,40% { opacity: 0; transform: translateY(6px); }
          100%   { opacity: 1; transform: translateY(0); }
        }
        .lc-container {
          display: flex; flex-direction: column; align-items: center;
          position: relative;
        }
        .lc-main {
          font-family: 'Cinzel Decorative', 'Times New Roman', serif;
          font-size: 44px;
          font-weight: 700;
          color: #ffd700;
          letter-spacing: 4px;
          text-shadow: 0 0 20px rgba(255,215,0,0.4), 0 0 40px rgba(255,170,0,0.2), 0 2px 8px rgba(0,0,0,0.8);
          animation: lcReveal 0.9s ease-out both, lcGlow 2.5s 1s infinite;
          position: relative;
          z-index: 2;
          white-space: nowrap;
        }
        .lc-echo {
          position: absolute;
          font-family: 'Cinzel Decorative', 'Times New Roman', serif;
          font-size: 44px;
          font-weight: 700;
          color: #ffd700;
          letter-spacing: 4px;
          white-space: nowrap;
          pointer-events: none;
          animation: lcEchoExpand 1.2s ease-out both;
          z-index: 1;
        }
        .lc-divider {
          display: flex; align-items: center; gap: 12px;
          margin-top: 8px;
          animation: lcSubReveal 1.2s ease-out both;
        }
        .lc-line {
          height: 1px;
          background: linear-gradient(to right, transparent, #ffd700, transparent);
          animation: lcLine 1s 0.4s ease-out both;
        }
        .lc-ornament {
          color: #ffd700;
          font-size: 16px;
          opacity: 0;
          animation: lcSubReveal 1.2s 0.5s ease-out both;
        }
      </style>
      <div class="lc-container">
        ${echosHTML}
        <div class="lc-main">${message}</div>
        <div class="lc-divider">
          <div class="lc-line"></div>
          <span class="lc-ornament">\u2694</span>
          <div class="lc-line"></div>
        </div>
      </div>
    `;
    document.body.appendChild(wrap);

    // Fade out before removal
    setTimeout(() => { wrap.style.animation = 'lcFadeOut 0.8s ease-in forwards'; }, 4200);
    setTimeout(() => wrap.remove(), 5000);
  }

  // ---- Per-Frame Update ----

  update(deltaTime) {
    // Always clean up damage numbers (they attach to sprite elements,
    // not the HUD container, so they exist even when HUD is hidden)
    this._updateDamageNumbers(deltaTime);
    if (!this.visible) return;
    this._updateHealthBar();
    this._updateResourceBars();
  }

  _updateDamageNumbers(deltaTime) {
    for (let i = this._damageNumbers.length - 1; i >= 0; i--) {
      const dn = this._damageNumbers[i];
      dn.age += deltaTime;
      if (dn.age >= dn.lifetime) {
        dn.element.remove();
        this._damageNumbers.splice(i, 1);
        continue;
      }
      // Animate: float up 40px over lifetime, fade out over 75% of lifetime
      const t = dn.age / dn.lifetime; // 0→1
      const y = -40 * t;
      const opacity = Math.max(0, 1 - t / 0.75);
      dn.element.style.transform = `translateX(-50%) translateY(${y}px)`;
      dn.element.style.opacity = String(opacity);
    }
  }

  // ---- Death Screen ----

  /**
   * Show death overlay with respawn button.
   * @param {Function} onRespawn - callback when player clicks respawn
   */
  showDeathScreen(onRespawn) {
    const existing = document.getElementById('death-screen');
    if (existing) existing.remove();

    const message = DEATH_MESSAGES[Math.floor(Math.random() * DEATH_MESSAGES.length)];

    // Build per-letter title spans
    const titleText = 'YOU DIED';
    let titleHTML = '';
    for (const ch of titleText) {
      if (ch === ' ') {
        titleHTML += '<span class="death-letter death-space">\u2003</span>';
      } else {
        titleHTML += `<span class="death-letter" data-ch="${ch}">${ch}</span>`;
      }
    }

    const overlay = document.createElement('div');
    overlay.id = 'death-screen';
    overlay.style.cssText = `
      position: fixed; inset: 0; z-index: 10001;
      background: rgba(20, 0, 0, 0.45);
      animation: deathFadeIn 1.2s ease-out;
    `;
    overlay.innerHTML = `
      <style>
        @keyframes deathFadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes deathGlow {
          0%,100% { text-shadow: 0 0 30px rgba(180,0,0,0.6), 0 0 60px rgba(120,0,0,0.3), 0 4px 12px rgba(0,0,0,0.9); }
          50%     { text-shadow: 0 0 50px rgba(220,38,38,0.9), 0 0 100px rgba(180,0,0,0.5), 0 4px 12px rgba(0,0,0,0.9); }
        }
        @keyframes letterReveal {
          0%   { opacity: 0; transform: translateY(-12px) scale(1.15); }
          60%  { opacity: 1; transform: translateY(2px) scale(1); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes subtitleFade {
          0%, 50% { opacity: 0; transform: translateY(10px); }
          100%    { opacity: 1; transform: translateY(0); }
        }
        #blood-canvas { position: absolute; inset: 0; pointer-events: none; }
        .death-content {
          position: relative; z-index: 1;
          display: flex; flex-direction: column;
          align-items: center; justify-content: center;
          width: 100%; height: 100%;
        }
        .death-title {
          display: flex;
          justify-content: center;
          margin: 0 0 8px;
          user-select: none;
          line-height: 1;
        }
        .death-letter {
          display: inline-block;
          position: relative;
          font-family: 'Cinzel Decorative', 'Times New Roman', serif;
          font-size: 76px;
          font-weight: 900;
          color: #dc2626;
          text-shadow: 0 0 30px rgba(180,0,0,0.6), 0 0 60px rgba(120,0,0,0.3), 0 4px 12px rgba(0,0,0,0.9);
          animation: letterReveal 0.6s ease-out both, deathGlow 3s 1.6s infinite;
        }
        .death-space { width: 0.35em; }
        .death-subtitle {
          font-family: 'Cinzel', 'Times New Roman', serif;
          font-size: 16px;
          font-style: italic;
          color: #a1a1aa;
          margin: 0 0 44px;
          text-shadow: 0 1px 6px rgba(0,0,0,0.9);
          animation: subtitleFade 2s ease-out forwards;
          text-align: center;
          max-width: 420px;
          line-height: 1.6;
          letter-spacing: 0.5px;
        }
        #death-respawn-btn {
          font-family: 'Cinzel', 'Times New Roman', serif;
          padding: 14px 44px;
          background: rgba(153,27,27,0.85);
          color: #fecaca;
          border: 2px solid #dc2626;
          border-radius: 8px;
          cursor: pointer;
          font-weight: 700;
          font-size: 16px;
          letter-spacing: 2px;
          text-transform: uppercase;
          transition: background 0.2s, transform 0.15s, box-shadow 0.2s;
          backdrop-filter: blur(4px);
          animation: subtitleFade 2.5s ease-out forwards;
        }
        #death-respawn-btn:hover {
          background: rgba(185,28,28,0.9);
          transform: scale(1.05);
          box-shadow: 0 0 24px rgba(220,38,38,0.5);
        }
      </style>
      <canvas id="blood-canvas"></canvas>
      <div class="death-content">
        <h1 class="death-title">${titleHTML}</h1>
        <p class="death-subtitle">${message}</p>
        <button id="death-respawn-btn">Respawn</button>
      </div>
    `;
    document.body.appendChild(overlay);

    // Stagger letter reveal
    const letters = overlay.querySelectorAll('.death-letter[data-ch]');
    letters.forEach((el, idx) => {
      el.style.animationDelay = `${idx * 0.08}s, 1.6s`;
    });

    // --- Canvas blood drip simulation ---
    const canvas = overlay.querySelector('#blood-canvas');
    const bCtx = canvas.getContext('2d');
    let sources = [];

    // Resize canvas to match viewport (uses setTransform so it's safe to call repeatedly)
    const resizeCanvas = () => {
      const dpr = window.devicePixelRatio || 1;
      canvas.width = window.innerWidth * dpr;
      canvas.height = window.innerHeight * dpr;
      canvas.style.width = window.innerWidth + 'px';
      canvas.style.height = window.innerHeight + 'px';
      bCtx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resizeCanvas();

    // Roll a size mode — persists for `remaining` drops before re-rolling.
    // Small modes produce bursts of many rapid drops; large modes last 1-2 drops.
    const rollMode = (isInner) => {
      const roll = Math.random();
      if (isInner) {
        if (roll < 0.70) return { accRate: 2.5 + Math.random() * 1.0, detachSize: 0.8 + Math.random() * 0.4, remaining: 4 + Math.floor(Math.random() * 5) };
        return { accRate: 1.0 + Math.random() * 0.5, detachSize: 1.4 + Math.random() * 0.4, remaining: 2 + Math.floor(Math.random() * 3) };
      }
      if (roll < 0.55) return { accRate: 2.2 + Math.random() * 1.0, detachSize: 0.9 + Math.random() * 0.5, remaining: 3 + Math.floor(Math.random() * 6) };
      if (roll < 0.85) return { accRate: 1.0 + Math.random() * 0.5, detachSize: 1.6 + Math.random() * 0.5, remaining: 2 + Math.floor(Math.random() * 3) };
      return { accRate: 0.5 + Math.random() * 0.3, detachSize: 2.6 + Math.random() * 0.4, remaining: 1 + Math.floor(Math.random() * 2) };
    };

    // Detect text edges (bottom + inner ceiling) and create drip sources
    const detectEdges = () => {
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const det = document.createElement('canvas');
      det.width = vw; det.height = vh;
      const dc = det.getContext('2d');
      dc.font = '900 76px "Cinzel Decorative", "Times New Roman", serif';
      dc.fillStyle = '#f00';
      dc.textBaseline = 'alphabetic';
      const fm = dc.measureText('M');
      const fontAsc = fm.fontBoundingBoxAscent || fm.actualBoundingBoxAscent || 60;
      const fontDesc = fm.fontBoundingBoxDescent || fm.actualBoundingBoxDescent || 16;
      const halfLeading = (76 - fontAsc - fontDesc) / 2;
      letters.forEach(el => {
        const r = el.getBoundingClientRect();
        dc.fillText(el.dataset.ch, r.left, r.top + halfLeading + fontAsc);
      });
      const imgData = dc.getImageData(0, 0, vw, vh).data;
      const pixel = (x, y) => (y >= 0 && y < vh) ? imgData[(y * vw + x) * 4 + 3] : 0;

      const titleRect = overlay.querySelector('.death-title').getBoundingClientRect();
      const scanL = Math.max(0, Math.floor(titleRect.left) - 4);
      const scanR = Math.min(vw - 1, Math.ceil(titleRect.right) + 4);
      const scanT = Math.max(0, Math.floor(titleRect.top) - 4);
      const scanB = Math.min(vh - 1, Math.ceil(titleRect.bottom) + 16);

      // For each column, find ALL opaque→transparent transitions (top to bottom).
      // Last transition = outer bottom edge. Earlier ones = inner ceiling edges
      // (e.g. the top of the hole in D, O, U, E).
      const bottomEdges = [];
      const innerEdges = [];
      for (let x = scanL; x <= scanR; x++) {
        const transitions = [];
        for (let y = scanT; y <= scanB; y++) {
          if (pixel(x, y) > 80 && pixel(x, y + 1) < 40) {
            transitions.push({ x, y });
          }
        }
        if (transitions.length > 0) {
          bottomEdges.push(transitions[transitions.length - 1]);
          for (let t = 0; t < transitions.length - 1; t++) {
            innerEdges.push(transitions[t]);
          }
        }
      }

      // Select local Y maxima (lowest points where liquid pools).
      // Collect all candidates first, then thin with random keep/skip
      // so drips cluster in some spots and leave gaps elsewhere.
      const selectPeaks = (edges, minSpacing) => {
        // Pass 1: find all local-maxima candidates (tiny min-spacing to avoid pure duplicates)
        const candidates = [];
        for (let i = 4; i < edges.length - 4; i++) {
          const e = edges[i];
          let isPeak = true;
          for (let d = -4; d <= 4; d++) {
            if (d !== 0 && edges[i + d] && edges[i + d].y > e.y) { isPeak = false; break; }
          }
          if (isPeak && !candidates.some(p => Math.abs(p.x - e.x) < 4)) {
            candidates.push(e);
          }
        }
        // Pass 2: walk candidates and randomly keep/skip to create organic clusters + gaps
        const kept = [];
        let i = 0;
        while (i < candidates.length) {
          if (Math.random() < 0.45) {
            // Keep this one — maybe keep the next neighbor too (cluster)
            kept.push(candidates[i]);
            if (i + 1 < candidates.length && Math.abs(candidates[i + 1].x - candidates[i].x) < minSpacing * 1.5 && Math.random() < 0.35) {
              kept.push(candidates[i + 1]);
              i += 2;
            } else {
              i++;
            }
          } else {
            // Skip — creates a gap
            i++;
          }
        }
        return kept;
      };

      const newSources = [];
      const addSource = (e, isInner) => {
        if (newSources.some(s => Math.abs(s.x - e.x) < 3 && Math.abs(s.y - e.y) < 3)) return;
        const mode = rollMode(isInner);
        const delayMax = isInner ? (mode.detachSize < 1.0 ? 1.2 : 1.8) : (mode.detachSize < 1.5 ? 1.5 : mode.detachSize < 2.2 ? 2.0 : 2.8);
        newSources.push({
          x: e.x, y: e.y + 1, acc: 0, isInner,
          accRate: mode.accRate, detachSize: mode.detachSize,
          remaining: mode.remaining,
          drops: [], delay: Math.random() * delayMax,
        });
      };

      for (const p of selectPeaks(bottomEdges, 10)) addSource(p, false);
      for (const p of selectPeaks(innerEdges, 14)) addSource(p, true);
      if (newSources.length > 28) {
        newSources.sort((a, b) => b.y - a.y);
        newSources.length = 28;
      }
      sources = newSources;
    };

    // Handle window resize: shift positions to follow centered text, preserve animation
    const onResize = () => {
      if (!overlay.isConnected) { window.removeEventListener('resize', onResize); return; }
      const oldW = parseFloat(canvas.style.width) || window.innerWidth;
      const oldH = parseFloat(canvas.style.height) || window.innerHeight;
      resizeCanvas();
      const newW = window.innerWidth;
      const newH = window.innerHeight;
      // Text is centered (flexbox), fixed 76px font — it shifts by half the viewport delta
      const dx = (newW - oldW) / 2;
      const dy = (newH - oldH) / 2;
      for (const s of sources) {
        s.x += dx;
        s.y += dy;
        for (const d of s.drops) {
          d.x += dx;
          d.y += dy;
          d.srcX += dx;
          d.srcY += dy;
        }
      }
    };
    window.addEventListener('resize', onResize);

    // Start simulation after letter reveal completes + font loaded
    setTimeout(() => {
      if (!overlay.isConnected) return;
      document.fonts.ready.then(() => {
      if (!overlay.isConnected) return;
      detectEdges();

      const GRAVITY = 250;
      let lastT = performance.now();

      // Draw teardrop: tip at (cx, tipY), bulge hangs below, total height h, half-width w
      const drawTeardrop = (cx, tipY, w, h) => {
        const widest = tipY + h * 0.55;
        const bot = tipY + h;
        bCtx.beginPath();
        bCtx.moveTo(cx, tipY);
        bCtx.bezierCurveTo(cx - w * 0.15, tipY + h * 0.15, cx - w, widest - h * 0.12, cx - w, widest);
        bCtx.bezierCurveTo(cx - w, widest + h * 0.2, cx - w * 0.55, bot, cx, bot);
        bCtx.bezierCurveTo(cx + w * 0.55, bot, cx + w, widest + h * 0.2, cx + w, widest);
        bCtx.bezierCurveTo(cx + w, widest - h * 0.12, cx + w * 0.15, tipY + h * 0.15, cx, tipY);
        bCtx.closePath();
      };

      const tick = () => {
        if (!overlay.isConnected) { window.removeEventListener('resize', onResize); return; }
        const now = performance.now();
        const dt = Math.min((now - lastT) / 1000, 0.05);
        lastT = now;
        const vh = window.innerHeight;
        const fadeZone = vh - 80; // start fading 80px from bottom
        bCtx.clearRect(0, 0, window.innerWidth, vh);

        for (const s of sources) {
          if (s.delay > 0) { s.delay -= dt; continue; }
          s.acc += s.accRate * dt;

          // Draw growing teardrop: tip pinned at text edge, bulge grows downward
          if (s.acc > 0.1) {
            const hw = 1.0 + s.acc * 1.2;
            const h = 2.0 + s.acc * 3.5;
            const g = bCtx.createRadialGradient(
              s.x - hw * 0.15, s.y + h * 0.5, hw * 0.1,
              s.x, s.y + h * 0.6, hw * 1.8
            );
            g.addColorStop(0, '#ef4444');
            g.addColorStop(0.4, '#dc2626');
            g.addColorStop(1, '#7f1d1d');
            bCtx.fillStyle = g;
            drawTeardrop(s.x, s.y, hw, h);
            bCtx.fill();
          }

          // Detach when big enough (per-source threshold)
          if (s.acc >= s.detachSize) {
            const hw = 1.0 + s.acc * 1.2;
            const h = 2.0 + s.acc * 3.5;
            const stLife = 0.08 + (s.detachSize / 3.0) * 0.18;
            s.drops.push({
              x: s.x, y: s.y + h * 0.55,
              vy: 6 + s.detachSize * 2,
              hw, h, opacity: 1,
              fadeRate: 0.15 + s.detachSize * 0.15, // small persist longer, large fade faster
              wobOff: Math.random() * Math.PI * 2,
              strandLife: stLife,
              srcX: s.x, srcY: s.y,
            });
            s.acc = 0.1;
            // Decrement mode counter; re-roll when exhausted
            s.remaining--;
            if (s.remaining <= 0) {
              const next = rollMode(s.isInner);
              s.accRate = next.accRate;
              s.detachSize = next.detachSize;
              s.remaining = next.remaining;
            }
          }

          // Update and draw falling drops
          for (let i = s.drops.length - 1; i >= 0; i--) {
            const d = s.drops[i];
            d.vy += GRAVITY * dt;
            d.y += d.vy * dt;
            d.opacity -= dt * d.fadeRate;
            d.strandLife -= dt;

            // Fade near bottom of screen
            if (d.y > fadeZone) {
              d.opacity = Math.min(d.opacity, 1 - (d.y - fadeZone) / 80);
            }

            if (d.y > vh + 10 || d.opacity <= 0) {
              s.drops.splice(i, 1);
              continue;
            }

            // Thin strand connecting back to text edge
            if (d.strandLife > 0) {
              const a = Math.min(1, d.strandLife * 6);
              bCtx.save();
              bCtx.globalAlpha = a;
              bCtx.strokeStyle = '#991b1b';
              bCtx.lineWidth = a * 1.2;
              bCtx.beginPath();
              bCtx.moveTo(d.srcX, d.srcY);
              bCtx.quadraticCurveTo(d.srcX, (d.srcY + d.y) * 0.5, d.x, d.y - d.h * 0.3);
              bCtx.stroke();
              bCtx.restore();
            }

            // Stretch with velocity (volume conservation)
            const stretch = Math.min(1 + d.vy / 130, 3);
            const squeeze = 1 / Math.sqrt(stretch);
            const dx = Math.sin(now * 0.003 + d.wobOff) * 0.2;
            const cx = d.x + dx;
            const fw = d.hw * squeeze;
            const fh = d.h * stretch;
            const tipY = d.y - fh * 0.45;

            bCtx.save();
            bCtx.globalAlpha = Math.max(0, d.opacity);
            const dg = bCtx.createRadialGradient(
              cx - fw * 0.15, tipY + fh * 0.5, fw * 0.1,
              cx, tipY + fh * 0.6, fw * 1.8
            );
            dg.addColorStop(0, '#f87171');
            dg.addColorStop(0.35, '#dc2626');
            dg.addColorStop(1, '#7f1d1d');
            bCtx.fillStyle = dg;
            drawTeardrop(cx, tipY, fw, fh);
            bCtx.fill();
            bCtx.restore();
          }
        }

        requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
      }); // document.fonts.ready
    }, 1200);

    // Respawn button
    const btn = overlay.querySelector('#death-respawn-btn');
    btn.addEventListener('click', () => {
      window.removeEventListener('resize', onResize);
      overlay.remove();
      if (onRespawn) onRespawn();
    });
  }

  hideDeathScreen() {
    const el = document.getElementById('death-screen');
    if (el) el.remove();
  }

  // ---- Void Respawn Screen (non-dungeon) ----

  /**
   * Show a minimal respawn overlay when the player falls into the void.
   * No death text or blood effects — just a centered respawn button.
   * @param {Function} onRespawn - callback when player clicks respawn
   */
  showVoidRespawnScreen(onRespawn) {
    const existing = document.getElementById('void-respawn-screen');
    if (existing) existing.remove();

    const overlay = document.createElement('div');
    overlay.id = 'void-respawn-screen';
    overlay.style.cssText = `
      position: fixed; inset: 0; z-index: 10001;
      background: rgba(0, 0, 0, 0);
      display: flex; align-items: center; justify-content: center;
      transition: background 0.8s ease;
    `;

    const btn = document.createElement('button');
    btn.textContent = 'RESPAWN';
    btn.style.cssText = `
      padding: 14px 48px;
      font-family: var(--font-primary, 'Inter', system-ui, sans-serif);
      font-size: 14px;
      font-weight: 600;
      letter-spacing: 2px;
      text-transform: uppercase;
      color: #c8e6c9;
      background: rgba(27, 94, 32, 0.7);
      border: 2px solid rgba(76, 175, 80, 0.6);
      border-radius: 6px;
      cursor: pointer;
      opacity: 0;
      transform: translateY(10px);
      transition: opacity 0.6s ease 0.4s, transform 0.6s ease 0.4s,
                  background 0.2s, border-color 0.2s;
    `;
    btn.addEventListener('mouseenter', () => {
      btn.style.background = 'rgba(46, 125, 50, 0.85)';
      btn.style.borderColor = 'rgba(129, 199, 132, 0.8)';
    });
    btn.addEventListener('mouseleave', () => {
      btn.style.background = 'rgba(27, 94, 32, 0.7)';
      btn.style.borderColor = 'rgba(76, 175, 80, 0.6)';
    });
    btn.addEventListener('click', () => {
      overlay.remove();
      if (onRespawn) onRespawn();
    });

    overlay.appendChild(btn);
    document.body.appendChild(overlay);

    // Trigger fade-in
    requestAnimationFrame(() => {
      overlay.style.background = 'rgba(0, 0, 0, 0.55)';
      btn.style.opacity = '1';
      btn.style.transform = 'translateY(0)';
    });
  }

  hideVoidRespawnScreen() {
    const el = document.getElementById('void-respawn-screen');
    if (el) el.remove();
  }

  // ---- Notifications ----

  /**
   * Show a brief text notification above the quickbar.
   * @param {string} text
   * @param {string} [color='#cdd6f4']
   */
  showNotification(text, color = '#cdd6f4') {
    const el = document.createElement('div');
    el.style.cssText = `
      position: fixed; bottom: 100px; left: 50%; transform: translateX(-50%);
      z-index: 5002; pointer-events: none;
      padding: 6px 16px; border-radius: 6px;
      background: rgba(30,30,46,0.85); border: 1px solid rgba(255,255,255,0.15);
      font-family: sans-serif; font-size: 13px; color: ${color};
      text-shadow: 0 1px 3px rgba(0,0,0,0.6);
      opacity: 1; transition: opacity 0.4s;
    `;
    el.textContent = text;
    document.body.appendChild(el);
    setTimeout(() => { el.style.opacity = '0'; }, 1500);
    setTimeout(() => el.remove(), 2000);
  }

  // ---- Interaction Prompt ----

  showChestPrompt(show) {
    if (!this._chestPrompt) {
      this._chestPrompt = document.createElement('div');
      this._chestPrompt.style.cssText = `
        position: fixed; bottom: 140px; left: 50%;
        transform: translateX(-50%); padding: 8px 20px;
        background: rgba(30, 30, 46, 0.9); color: #cba6f7;
        border: 1px solid #585b70; border-radius: 8px;
        font-family: sans-serif; font-size: 14px;
        pointer-events: none; z-index: 5000;
        transition: opacity 0.2s;
      `;
      const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      this._chestPrompt.textContent = isTouchDevice ? 'Press X to open chest' : 'Press E to open chest';
      // Append to game container (not HUD container) so it shows even when HUD is hidden
      const gameContainer = document.getElementById('container');
      (gameContainer || document.body).appendChild(this._chestPrompt);
    }
    this._chestPrompt.style.opacity = show ? '1' : '0';
  }

  // ---- Cleanup ----

  destroy() {
    this.container.remove();
    if (this._resourceBars) this._resourceBars.remove();
    this._damageNumbers = [];
  }
}
